import { useState, useEffect } from "react";
import styles from "./asset/signInForm.module.scss";
import { useNavigate, Link } from "react-router-dom";
import { validateForm, signInValidationRules } from "../../utils/formValidation.js";
import { useDispatch, useSelector } from "react-redux";
import { userLoading$, userError$, usersData$, userCookieConfirmed$ } from "../../reduxStore/selectors/userSelectors.js";
import * as userActions from "../../reduxStore/actions/userActionsHelper.js";
import { clearError } from "../../reduxStore/reducers/userReducersHelper.js";
import TermsOfServiceModal from "../termsOfServiceModal/termsOfServiceModal.js";
import PrivacyPolicyModal from "../privacyPolicyModal/privacyPolicyModal.js";
import * as userApi from "../../api/index.js";
import LoadingSpinner from "../loadingSpinner/loadingSpinner.js";
import MessageConstant from "../../utils/Constant/MessageConstant.js";
import { isFeatureEnabled, FEATURES } from "../../utils/featureFlags.js";

const SignInFormComponent = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [signInData, setSignInData] = useState({});
  const [errors, setErrors] = useState({});
  const [loginSubmitted, setLoginSubmitted] = useState(false);
  const [showTosModal, setShowTosModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const reduxIsLoading = useSelector(userLoading$);
  const reduxError = useSelector(userError$);
  const userData = useSelector(usersData$);
  const cookieConfirmed = useSelector(userCookieConfirmed$);

  // Loading state: true until cookie is confirmed
  const isLoading = reduxIsLoading || (userData && loginSubmitted && !cookieConfirmed);

  // Check if user is already authenticated on mount
  useEffect(() => {
    const checkExistingAuth = async () => {
      try {
        const response = await userApi.checkAuthStatus();

        if (response.data.authenticated && response.data.user) {
          // User is already authenticated, update Redux and redirect
          dispatch(userActions.loginUser.success(response.data.user));
          navigate("/user-dashboard", { replace: true });
        }
      } catch (error) {
        // Not authenticated or error - just show login form
        console.log('Not authenticated, showing login form');
      } finally {
        setIsCheckingAuth(false);
      }
    };

    checkExistingAuth();
  }, [dispatch, navigate]);

  useEffect(() => {
    if (reduxError) {
      setErrors({ general: reduxError });
      dispatch(clearError());
      setLoginSubmitted(false);
    }
  }, [reduxError, dispatch]);

  // Navigate when cookie is confirmed
  useEffect(() => {
    if (userData && loginSubmitted && !reduxIsLoading && !reduxError && cookieConfirmed) {
      console.log('✅ Cookie confirmed by Redux state, navigating to dashboard...');
      navigate("/user-dashboard");
      setLoginSubmitted(false);
    }
  }, [userData, loginSubmitted, reduxIsLoading, reduxError, cookieConfirmed, navigate]);

  const validateFormData = () => {
    setErrors({});
    const validation = validateForm(signInData, signInValidationRules);
    setErrors(validation.errors);
    return validation.isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateFormData()) {
      return;
    }

    setErrors({});
    setLoginSubmitted(true);

    dispatch(userActions.loginUser.request(signInData));
  };

  // Show loading while checking authentication status
  if (isCheckingAuth) {
    return <LoadingSpinner message={MessageConstant.INITIALIZING} />;
  }

  return (
    <div className={styles.signInForm}>
      <div className={styles.signInChild}>
        <div className={styles.aceYourTestContainer}>
          <p className={styles.aceYourTest}>Ace your test</p>
          <p className={styles.aceYourTest}>with Prepal</p>
        </div>
        <img className={styles.maskGroupIcon}
          alt=""
          src={require("../../publicAsset/media/book 1.svg").default}
        />
      </div>
      <div className={styles.prepalLogo3Parent}>
        <img
          className={styles.prepalLogo3}
          alt=""
          src={require("../../publicAsset/media/Prepal Logo 3.svg").default}
        />
        <div className={styles.frameParent}>
          <div className={styles.letsSignInWrapper}>
            <div className={styles.letsSignIn}>Let's sign in</div>
          </div>
          <form className={styles.frameGroup} onSubmit={handleSubmit}>
            <div className={styles.frameWrapper}>
              <div className={styles.inputParent}>
                {errors.general && (
                  <div style={{ color: 'red', fontSize: '0.875rem', marginBottom: '1rem' }}>
                    {errors.general}
                  </div>
                )}
                <div className={styles.input}>
                  <input
                    className={styles.yourEmailAndPassword}
                    name="email"
                    type="email"
                    placeholder="Your Email"
                    required
                    onChange={(e) =>
                      setSignInData({ ...signInData, email: e.target.value })
                    }
                    style={{ borderColor: errors.email ? 'red' : '' }}
                  />
                </div>
                {errors.email && (
                  <div style={{ color: 'red', fontSize: '0.875rem', marginTop: '-0.5rem' }}>
                    {errors.email}
                  </div>
                )}
                <div className={styles.input}>
                  <input
                    className={styles.yourEmailAndPassword}
                    name="password"
                    type="password"
                    placeholder="Your Password"
                    required
                    onChange={(e) =>
                      setSignInData({ ...signInData, password: e.target.value })
                    }
                    style={{ borderColor: errors.password ? 'red' : '' }}
                  />
                </div>
                {errors.password && (
                  <div style={{ color: 'red', fontSize: '0.875rem', marginTop: '-0.5rem' }}>
                    {errors.password}
                  </div>
                )}
                <div className={styles.frameContainer}>
                  <div className={styles.checkBoxParent}>
                    <div className={styles.checkBox}>
                      <input
                        type="checkbox"
                        id="rememberMe"
                        name="rememberMe"
                        onChange={(e) =>
                          setSignInData({
                            ...signInData,
                            rememberMe: e.target.checked,
                          })
                        }
                      />
                    </div>
                    <label htmlFor="rememberMe" className={styles.rememberMe}>
                      Remember me
                    </label>
                  </div>
                  <button
                    type="button"
                    className={styles.didYouForget}
                    onClick={(e) => {
                      e.preventDefault();
                      navigate("/forgot-password");
                    }}
                  >
                    Did you forget your password?
                  </button>
                </div>
              </div>
            </div>
            <div className={styles.buttonsParent}>
              <button
                type="submit"
                className={styles.buttons}
                disabled={isLoading}
              >
                <div className={styles.signInBtn}>
                  {isLoading ? "Signing In..." : "Sign In"}
                </div>
              </button>
              {isFeatureEnabled(FEATURES.SOCIAL_SIGNIN) && (
                <>
                  <div className={styles.lineParent}>
                    <div className={styles.groupChild} />
                    <div className={styles.orWrapper}>
                      <div className={styles.or}>Or</div>
                    </div>
                  </div>
                  <div className={styles.socialSignInParent}>
                    <div className={styles.socialSignIn}>
                      <img
                        className={styles.socialIcons}
                        alt="Google Icons.svg"
                        src={require("../../publicAsset/media/Platform=Google, Color=Original.svg").default}
                      />
                      <div className={styles.yourEmail}>With Google</div>
                    </div>
                    <div className={styles.socialSignIn}>
                      <img
                        className={styles.socialIcons}
                        alt="Facebook Icons.svg"
                        src={require("../../publicAsset/media/Platform=Facebook, Color=Original.svg").default}
                      />
                      <div className={styles.yourEmail}>With Facebook</div>
                    </div>
                  </div>
                </>
              )}
              <div className={styles.byContinuingYouContainer}>
                <p className={styles.aceYourTest}>
                  {`By continuing, you agree to our `}
                  <span className={styles.termsOfUse} onClick={() => setShowTosModal(true)}>Terms of Use</span>
                </p>
                <p className={styles.aceYourTest}>
                  {`and `}
                  <span className={styles.termsOfUse} onClick={() => setShowPrivacyModal(true)}>Privacy Policy</span>
                </p>
              </div>
            </div>
          </form>
        </div>
        <div className={styles.dontHaveAnAccountParent}>
          <div className={styles.dontHaveAn}>{`Don't have an account? `}</div>
          <div className={styles.signUpWrapper}>
            <Link
              to="/sign-up"
              className={styles.signUp}
            >
              Sign Up
            </Link>
          </div>
        </div>

        {/* Modals */}
        <TermsOfServiceModal isOpen={showTosModal} onClose={() => setShowTosModal(false)} />
        <PrivacyPolicyModal isOpen={showPrivacyModal} onClose={() => setShowPrivacyModal(false)} />
      </div>
    </div>
  );
};

export default SignInFormComponent;
